import moment from 'moment';
import { canAccessAdminFeatures } from './authPermissions';

export const I18N = 'sections.dashboard-individual.forecast-confidence';

const PERIOD_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const isMissing = (value) => value === null || value === undefined;

/**
 * Turns the dashboard period (browser-local 'YYYY-MM-DD HH:mm:ss' strings,
 * the end being the last minute of the day) into the UTC instants the
 * forecast-confidence endpoints expect. The end becomes exclusive: 23:59 is
 * rounded up to the next day's 00:00. Returns null for an incomplete period.
 */
export const toUtcRange = (period) => {
    if (!period?.[0] || !period?.[1]) return null;
    const from = moment(period[0], PERIOD_FORMAT);
    const to = moment(period[1], PERIOD_FORMAT)
        .add(1, 'minute')
        .startOf('minute');
    if (!from.isValid() || !to.isValid() || !from.isBefore(to)) return null;
    return { from: from.toISOString(), to: to.toISOString() };
};

/** 0.8734 → "87.3%"; null → "—". */
export const formatConfidence = (ratio) =>
    isMissing(ratio) ? '—' : `${(Number(ratio) * 100).toFixed(1)}%`;

/** Integer counts; null (unknown) → "—", never 0. */
export const formatCount = (value) =>
    isMissing(value) ? '—' : Number(value).toLocaleString('es-AR');

/** Average seconds between creative requests → "~45 s" / "~3 min"; null → "—". */
export const formatRequestInterval = (seconds, t) => {
    if (isMissing(seconds)) return '—';
    const value = Number(seconds);
    if (value < 60) {
        return t(`${I18N}.interval-seconds`, { value: Math.round(value) });
    }
    return t(`${I18N}.interval-minutes`, { value: Math.round(value / 60) });
};

/**
 * Regularity cell text: the pattern label plus the share of hours with
 * requests ("Cada 2 horas · 50%"). `no_data` has no percentage.
 */
export const getRegularityLabel = (regularity, t) => {
    const pattern = regularity?.pattern || 'no_data';
    const label =
        pattern === 'every_n_hours'
            ? t(`${I18N}.regularity.every_n_hours`, {
                  hours: regularity.regular_gap_hours,
              })
            : t(`${I18N}.regularity.${pattern}`);
    if (pattern === 'no_data' || isMissing(regularity?.presence_ratio)) {
        return label;
    }
    return `${label} · ${Math.round(regularity.presence_ratio * 100)}%`;
};

/** Tooltip with the median and the longest gap between hours with requests. */
export const getRegularityTooltip = (regularity, t) => {
    if (isMissing(regularity?.median_gap_hours)) return '';
    return t(`${I18N}.regularity.tooltip`, {
        median: regularity.median_gap_hours,
        max: regularity.max_gap_hours,
    });
};

const HOUR_STATUS_STYLES = {
    met: 'bg-green-100 text-green-800',
    missed_opportunity: 'bg-red-100 text-red-700 font-semibold',
    no_supply: 'bg-yellow-100 text-yellow-800',
    no_request_data: 'bg-gray-100 text-gray-600',
    unplanned: 'bg-blue-100 text-blue-800',
    not_planned: 'text-gray-400',
    in_progress: 'bg-gray-100 text-gray-600 italic',
};

/** i18n key and Tailwind classes for an hour status. */
export const getHourStatusStyle = (status) => ({
    labelKey: `${I18N}.status.${
        HOUR_STATUS_STYLES[status] ? status : 'not_planned'
    }`,
    className: HOUR_STATUS_STYLES[status] || HOUR_STATUS_STYLES.not_planned,
    isProblem: status === 'missed_opportunity',
});

/**
 * Notice shown when request data stops before the evaluated range does.
 * Returns null when requests cover the whole range.
 */
export const getRequestsCoverageNotice = (
    {
        requests_data_until: requestsUntil,
        evaluated_until: evaluatedUntil,
    } = {},
    t
) => {
    if (!requestsUntil) return t(`${I18N}.coverage.unavailable`);
    if (!evaluatedUntil || !moment(requestsUntil).isBefore(evaluatedUntil)) {
        return null;
    }
    return t(`${I18N}.coverage.partial`, {
        until: moment(requestsUntil).format('DD/MM HH:mm'),
    });
};

const readValue = (row, selector) =>
    typeof selector === 'function' ? selector(row) : row?.[selector];

/**
 * DataTable `sortFunction`: sorts by the column selector and keeps empty
 * values (null confidence, unknown requests) last in both directions.
 */
export const sortRowsNullLast = (rows, selector, direction) => {
    const factor = direction === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
        const left = readValue(a, selector);
        const right = readValue(b, selector);
        if (isMissing(left) && isMissing(right)) return 0;
        if (isMissing(left)) return 1;
        if (isMissing(right)) return -1;
        if (typeof left === 'number' && typeof right === 'number') {
            return (left - right) * factor;
        }
        return String(left).localeCompare(String(right)) * factor;
    });
};

/**
 * Axios config for the admin-only endpoints: the admin's token while an
 * admin is supervising, otherwise undefined (the session token is used).
 */
export const getForecastConfidenceRequestConfig = (auth) => {
    if (!auth?.isSupervising?.() || !canAccessAdminFeatures(auth)) {
        return undefined;
    }
    const adminSession = auth.getAdminSessionFromLocalStorage?.();
    if (!adminSession?.token) return undefined;
    return { headers: { Authorization: `Bearer ${adminSession.token}` } };
};

/** Chart/table label of an hour, in browser local time. */
export const formatHourLabel = (hourUtc, withDay) =>
    moment(hourUtc).format(withDay ? 'DD/MM HH:00' : 'HH:00');
